import React, { useEffect, useRef, useState } from 'react';
import { toastService } from '../../services/ToastService';
import { createProduct, updateProduct } from '../../services/api';
import { handleApiErrorWithToast } from '../../utils/errorHandling';
import { BUSINESS_CATEGORIES, BUSINESS_SUBCATEGORIES, getSubcategories } from '../../constants/categories';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import RequiredFieldIndicator from '../ui/RequiredFieldIndicator';
import { handleLimitExceeded, checkLimitsBeforeSubmission } from '../../utils/limitHandler';
import LimitExceededModal from '../subscription/LimitExceededModal';
import subscriptionService from '../../services/subscriptionService';

const CustomProductForm = ({ 
  categories = [], 
  onSuccess, 
  onCancel,
  editingProduct = null
}) => {
  const formRef = useRef(null);
  const nameInputRef = useRef(null);
  const skuInputRef = useRef(null);
  const descriptionInputRef = useRef(null);
  const categorySelectRef = useRef(null);
  const priceInputRef = useRef(null);
  const costPriceInputRef = useRef(null);
  const quantityInputRef = useRef(null);
  const lowStockThresholdInputRef = useRef(null);
  const barcodeInputRef = useRef(null);

  // Add state for form data
  const [formData, setFormData] = React.useState({
    name: '',
    sku: '',
    description: '',
    category: '',
    subcategory: '',
    price: '',
    cost_price: '',
    quantity: '',
    low_stock_threshold: '5',
    barcode: ''
  });

  const [loading, setLoading] = React.useState(false);
  
  // Limit exceeded modal state
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [limitModalData, setLimitModalData] = useState(null);

  // Categories and subcategories are now imported from shared constants

  useEffect(() => {
    // Focus on first input
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  // Populate form with editing data
  useEffect(() => {
    if (editingProduct) {
      // Set form values
      if (nameInputRef.current) nameInputRef.current.value = editingProduct.name || '';
      if (skuInputRef.current) skuInputRef.current.value = editingProduct.sku || '';
      if (descriptionInputRef.current) descriptionInputRef.current.value = editingProduct.description || '';
      if (priceInputRef.current) priceInputRef.current.value = editingProduct.price || editingProduct.unit_price || '';
      if (costPriceInputRef.current) costPriceInputRef.current.value = editingProduct.cost_price || '';
      if (quantityInputRef.current) quantityInputRef.current.value = editingProduct.quantity || '';
      if (lowStockThresholdInputRef.current) lowStockThresholdInputRef.current.value = editingProduct.low_stock_threshold || '5';
      if (barcodeInputRef.current) barcodeInputRef.current.value = editingProduct.barcode || '';

      // Set form data state
      setFormData({
        name: editingProduct.name || '',
        sku: editingProduct.sku || '',
        description: editingProduct.description || '',
        category: editingProduct.category || '',
        subcategory: editingProduct.subcategory || '',
        price: editingProduct.price || editingProduct.unit_price || '',
        cost_price: editingProduct.cost_price || '',
        quantity: editingProduct.quantity || '0',
        low_stock_threshold: editingProduct.low_stock_threshold || '5',
        barcode: editingProduct.barcode || ''
      });
    }
  }, [editingProduct]);

  // Show limit exceeded modal
  const showLimitModal = (limitData) => {
    setLimitModalData(limitData);
    setLimitModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check limits before submission (only for new products, not edits)
    if (!editingProduct) {
      const canCreate = await checkLimitsBeforeSubmission(
        'products',
        subscriptionService.getUsageStatus,
        showLimitModal
      );
      
      if (!canCreate) {
        return; // Limit exceeded, don't proceed
      }
    }

    setLoading(true);

    // Always use the latest value from formData for quantity
    const submitData = {
      name: formData.name.trim(),
      sku: formData.sku.trim(),
      description: formData.description.trim(),
      category: formData.category,
      subcategory: formData.subcategory,
      price: parseFloat(formData.price) || 0,
      cost_price: parseFloat(formData.cost_price) || 0,
      quantity: parseInt(formData.quantity) || 0,
      low_stock_threshold: parseInt(formData.low_stock_threshold) || 5,
      barcode: formData.barcode.trim() || null
    };

    try {
      const { validateProductData } = await import('../../utils/productValidator');
      const validation = validateProductData(submitData);
      if (!validation.isValid) {
        const firstErrorKey = Object.keys(validation.errors)[0];
        const firstError = validation.errors[firstErrorKey];
        toastService.error(firstError);
        return;
      }
      let response;
        if (editingProduct) {
          response = await updateProduct(editingProduct.id, validation.formattedData);
        toastService.success('Product updated successfully!');
        } else {
          response = await createProduct(validation.formattedData);
        toastService.success('Product created successfully!');
      }
      setFormData({
        name: '', sku: '', description: '', category: '', subcategory: '', price: '', cost_price: '', quantity: '', low_stock_threshold: '5', barcode: ''
      });
      if (onSuccess) onSuccess(response);
    } catch (error) {
      console.error('Error creating/updating product:', error);
      
      // Handle limit exceeded errors from backend
      if (error.response && error.response.data) {
        const handled = handleLimitExceeded(error.response.data, showLimitModal);
        if (!handled) {
          toastService.error(error?.response?.data?.message || error.message || 'Failed to create product');
        }
      } else {
        toastService.error(error?.response?.data?.message || error.message || 'Failed to create product');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputFocus = (inputName) => {};

  const handleInputBlur = (inputName) => {};

  const handleInputChange = (inputName, value) => {
    setFormData(prev => ({ ...prev, [inputName]: value }));
  };

  return (
    <div className="custom-product-form">
      <style jsx>{`
        .custom-product-form {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        
        .custom-product-form form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        
        .form-row {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
        }
        
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .form-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.25rem;
        }
        
        .form-input {
          height: 3rem;
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          font-size: 1rem;
          background-color: white;
          transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
          touch-action: manipulation;
        }
        
        .form-input:focus {
          outline: none;
          border-color: hsl(142 76% 36%);
          box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
        }
        
        .form-textarea {
          min-height: 6rem;
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          font-size: 1rem;
          background-color: white;
          resize: vertical;
          transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
          touch-action: manipulation;
        }
        
        .form-textarea:focus {
          outline: none;
          border-color: hsl(142 76% 36%);
          box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
        }
        
        .form-select {
          height: 3rem;
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          font-size: 1rem;
          background-color: white;
          transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
          touch-action: manipulation;
        }
        
        .form-select:focus {
          outline: none;
          border-color: hsl(142 76% 36%);
          box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
        }
        
        .form-buttons {
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
          padding-top: 1rem;
          border-top: 1px solid #e5e7eb;
        }
        
        .btn {
          padding: 0.75rem 1.5rem;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          font-weight: 500;
          transition: all 0.15s ease-in-out;
          cursor: pointer;
          border: none;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          min-height: 3rem;
          touch-action: manipulation;
        }
        
        .btn-primary {
          background-color: hsl(142 76% 36%);
          color: white;
        }
        
        .btn-primary:hover {
          background-color: hsl(142 76% 28%);
        }
        
        .btn-secondary {
          background-color: #6b7280;
          color: white;
        }
        
        .btn-secondary:hover {
          background-color: #4b5563;
        }
        
        .btn-outline {
          background-color: transparent;
          color: #6b7280;
          border: 1px solid #d1d5db;
        }
        
        .btn-outline:hover {
          background-color: #f9fafb;
          border-color: #9ca3af;
        }
        
        @media (min-width: 640px) {
          .form-row {
            grid-template-columns: 1fr 1fr;
          }
        }
        
        @media (max-width: 639px) {
          .form-row {
            grid-template-columns: 1fr;
          }
          
          .form-input,
          .form-select,
          .form-textarea {
            font-size: 16px; /* Prevents zoom on iOS */
            padding: 0.75rem;
            height: auto;
            min-height: 3rem;
          }
          
          .form-buttons {
            flex-direction: column;
            gap: 0.75rem;
          }
          
          .btn {
            width: 100%;
          }
        }
      `}</style>

      <form ref={formRef} onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label flex items-center gap-1">
              Product Name
              <RequiredFieldIndicator />
            </label>
            <input
              ref={nameInputRef}
              type="text"
              className="form-input"
              placeholder="Enter product name"
              required
              onFocus={() => handleInputFocus('name')}
              onBlur={() => handleInputBlur('name')}
              onChange={(e) => handleInputChange('name', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">SKU</label>
            <input
              ref={skuInputRef}
              type="text"
              className="form-input"
              placeholder="Product SKU (optional)"
              onFocus={() => handleInputFocus('sku')}
              onBlur={() => handleInputBlur('sku')}
              onChange={(e) => handleInputChange('sku', e.target.value)}
            />
          </div>
        </div>



        <div className="form-row">
          <div className="form-group">
            <label className="form-label flex items-center gap-1">
              Category
              <RequiredFieldIndicator />
            </label>
            <Select 
              value={formData.category} 
              onValueChange={(value) => handleInputChange('category', value)}
            >
              <SelectTrigger className="form-select">
                <SelectValue 
                  placeholder="Select a category"
                  value={formData.category}
                />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {BUSINESS_CATEGORIES.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>


        </div>



        <div className="form-row">
          <div className="form-group">
            <label className="form-label flex items-center gap-1">
              Selling Price (₦)
              <RequiredFieldIndicator />
            </label>
            <input
              ref={priceInputRef}
              type="number"
              step="0.01"
              min="0"
              className="form-input"
              placeholder="0.00"
              required
              onFocus={() => handleInputFocus('price')}
              onBlur={() => handleInputBlur('price')}
              onChange={(e) => handleInputChange('price', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Cost Price (₦)</label>
            <input
              ref={costPriceInputRef}
              type="number"
              step="0.01"
              min="0"
              className="form-input"
              placeholder="0.00"
              onFocus={() => handleInputFocus('cost_price')}
              onBlur={() => handleInputBlur('cost_price')}
              onChange={(e) => handleInputChange('cost_price', e.target.value)}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label flex items-center gap-1">
              Stock Quantity
              <RequiredFieldIndicator />
            </label>
            <Input
              id="quantity"
              name="quantity"
              type="number"
              value={formData.quantity === undefined ? '' : formData.quantity}
              onChange={(e) => handleInputChange('quantity', e.target.value)}
              placeholder="Quantity"
              min={1}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Low Stock Threshold</label>
            <input
              ref={lowStockThresholdInputRef}
              type="number"
              min="0"
              className="form-input"
              placeholder="5"
              defaultValue="5"
              onFocus={() => handleInputFocus('low_stock_threshold')}
              onBlur={() => handleInputBlur('low_stock_threshold')}
              onChange={(e) => handleInputChange('low_stock_threshold', e.target.value)}
            />
          </div>
        </div>

        {/* Form Buttons */}
        <div className="form-buttons grid grid-cols-2 gap-2 sm:flex sm:gap-3 sm:justify-end">
          <button
            type="button"
            className="btn btn-outline"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (editingProduct ? 'Updating...' : 'Creating...') : (editingProduct ? 'Update Product' : 'Create Product')}
          </button>
        </div>
      </form>

      {/* Limit Exceeded Modal */}
      <LimitExceededModal
        isOpen={limitModalOpen}
        onClose={() => setLimitModalOpen(false)}
        featureType={limitModalData?.featureType}
        currentUsage={limitModalData?.currentUsage}
        limit={limitModalData?.limit}
        currentPlan={limitModalData?.currentPlan}
        suggestedPlans={limitModalData?.suggestedPlans}
      />
    </div>
  );
};

export default CustomProductForm; 